// pages/mobile/AnswerBar.js
// The bottom answer bar shared by the dispatch, warning and request pages
// (mockups 2b, 2c, 2d, 3b, 3c): confirm, decline with an optional note,
// and update an earlier answer while the server still allows it.
import React, { useEffect, useState } from 'react';
import { CheckIcon } from './icons';
import { answerLabel } from './format';
import { errorMessage, sendAnswer } from './mobileApi';
import styles from './mobile.module.css';

const AnswerBar = ({ kind, id, status, needsAnswer, labels, allowChange = true, canAnswer = true, closedReason, onChanged }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [note, setNote] = useState('');
  const [changing, setChanging] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => setChanging(false), [id, kind, status, canAnswer]);

  const answer = async (choice) => {
    setSending(true);
    setError('');
    try {
      await sendAnswer(kind, id, choice, choice === 'deny' ? note : '');
      setSheetOpen(false);
      setChanging(false);
      setNote('');
      setNotice(choice === 'confirm' ? labels.confirmedToast : labels.deniedToast);
      onChanged();
    } catch (requestError) {
      setError(errorMessage(requestError));
      onChanged();
    } finally {
      setSending(false);
    }
  };

  const answered = answerLabel(status);
  const showButtons = canAnswer && (needsAnswer || changing);

  return (
    <>
      <div className={styles.actionBar}>
        {error && !sheetOpen && <p role="alert" className={styles.fieldError}>{error}</p>}
        {!canAnswer ? <p role="status" className={styles.actionHint}>{closedReason || 'This message is closed.'}</p> : showButtons ? (
          <>
            {labels.question
              ? <p className={styles.actionQuestion}>{labels.question}</p>
              : <p className={styles.actionHint}>Dispatch sees your answer right away</p>}
            <div className={labels.deny ? styles.actionRow : undefined}>
              {labels.deny && (
                <button type="button" className={styles.secondaryButton} disabled={sending} onClick={() => setSheetOpen(true)}>
                  {labels.deny}
                </button>
              )}
              <button
                type="button"
                className={styles.primaryButton}
                disabled={sending}
                onClick={() => answer('confirm')}
                style={labels.deny ? undefined : { width: '100%' }}
              >
                <CheckIcon />
                {labels.confirm}
              </button>
            </div>
            {labels.footnote && <p className={styles.actionHint}>{labels.footnote}</p>}
          </>
        ) : (
          <>
            <div className={answered === 'denied' ? `${styles.answeredBanner} ${styles.answeredBannerDenied}` : styles.answeredBanner}>
              {answered === 'denied' ? labels.deniedState : labels.confirmedState}
            </div>
            {allowChange && answered && canAnswer && (
              <button type="button" className={styles.textButton} onClick={() => setChanging(true)}>
                Something changed? Update my answer
              </button>
            )}
          </>
        )}
      </div>

      {notice && (
        <div className={styles.toast} role="status">
          <CheckIcon size={18} />
          <span className={styles.toastText}>{notice}</span>
        </div>
      )}

      {sheetOpen && canAnswer && (
        <>
          <div className={styles.sheetBackdrop} onClick={() => setSheetOpen(false)} aria-hidden="true" />
          <div role="dialog" aria-modal="true" aria-labelledby="deny-title" className={styles.sheet}>
            <div className={styles.sheetHandle} />
            <div>
              <h2 id="deny-title" className={styles.sheetTitle}>{labels.denyTitle}</h2>
              <p className={styles.mutedText}>Dispatch will see you as not available.</p>
            </div>
            <label htmlFor="deny-note" className={styles.fieldLabel}>Note for dispatch (optional)</label>
            <textarea
              id="deny-note"
              className={styles.textArea}
              rows={5}
              maxLength={500}
              placeholder="Let dispatch know why, if you want to."
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            {error && <p role="alert" className={styles.fieldError}>{error}</p>}
            <button type="button" className={styles.dangerButton} disabled={sending} onClick={() => answer('deny')}>
              {labels.denySubmit}
            </button>
            <button type="button" className={styles.textButton} onClick={() => setSheetOpen(false)}>Go back</button>
          </div>
        </>
      )}
    </>
  );
};

export default AnswerBar;
